/**CORE LIBRARIES IMPORT */
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/**COMPONENTS IMPORT */
import BrandLogo from "./BrandLogo";

/**DATA IMPORT */
import { site } from "../data/site";
import { navLinks, BOOK_TABLE_PATH } from "../data/navigation";


const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const menuBtnRef = useRef(null);
  const firstLinkRef = useRef(null);
  const wasOpen = useRef(false);

  const current = `${location.pathname}${location.hash}`;
  const isCurrent = (path) => (path === "/" ? current === "/" : current.startsWith(path));

  const links = [...navLinks, { label: "Book a Table", path: BOOK_TABLE_PATH }];

  useEffect(() => {
  setIsOpen(false);
}, [location.pathname, location.hash]);

  const isHome = location.pathname === "/";
  const headerRef = useRef(null);

  // Expose the header height so sticky elements (menu category bar) sit below it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty("--header-h", `${Math.round(entry.contentRect.height)}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
  const handleScroll = () => {
    // Home: template behaviour (solid after the full-height hero).
    // Inner pages have short heroes, so go solid after a small scroll.
    const threshold = isHome ? window.innerHeight - 80 : 40;
    setIsScrolled(window.scrollY > threshold);

  };

  window.addEventListener("scroll", handleScroll, { passive: true });


  handleScroll();

  return () => {
    window.removeEventListener("scroll", handleScroll);
  };
}, [isHome]);

  // Escape closes the menu; focus moves into the menu on open and back to
  // the toggle button on close.
  useEffect(() => {
    if (isOpen) {
      firstLinkRef.current?.focus();
      const onKey = (e) => e.key === "Escape" && setIsOpen(false);
      window.addEventListener("keydown", onKey);
      wasOpen.current = true;
      return () => window.removeEventListener("keydown", onKey);
    }
    if (wasOpen.current) {
      menuBtnRef.current?.focus();
      wasOpen.current = false;
    }
  }, [isOpen]);

  return (
    <>
    <motion.header
  ref={headerRef}
  className={`header ${isScrolled ? "scrolled" : ""}`}
  initial={{ y: -80, opacity: 0 }}
  animate={{ y: 0, opacity: 1 }}
  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
  style={{ position: isScrolled ? "fixed" : "absolute" }}
>

       <div className="header-container">
    <div className="logo">
      <Link to='/' className="logo-link" aria-label={`${site.name}, home`}>
        <BrandLogo size={52} className="logo-emblem" decorative eager />
        <span>{site.name}</span>
      </Link>
    </div>

 <motion.button
  ref={menuBtnRef}
  type="button"
  className="menu-btn"
  onClick={() => setIsOpen(!isOpen)}
  aria-label={isOpen ? "Close menu" : "Open menu"}
  aria-expanded={isOpen}
  aria-controls="site-menu"
  animate={{ rotate: isOpen ? 90 : 0 }}
  transition={{ duration: 0.3 }}
>
  <span aria-hidden="true">{isOpen ? "✕" : "☰"}</span>
</motion.button>
  </div>
      </motion.header>

      <AnimatePresence>
  {isOpen && (
    <>
      <motion.div
        id="site-menu"
        className="sidebar"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="sidebar-top">
          <div className="logo">
            <span className="logo-link">
              <BrandLogo size={52} className="logo-emblem" decorative />
              <span>{site.name}</span>
            </span>
          </div>
        </div>

        <motion.nav
          className="sidebar-links"
          aria-label="Main"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          {links.map((item, index) => (
  <motion.div
    key={item.label}
    variants={{
      hidden: { opacity: 0, x: 40 },
      visible: { opacity: 1, x: 0 }
    }}
    transition={{ duration: 0.5 }}
  >
    <Link
      to={item.path}
      ref={index === 0 ? firstLinkRef : undefined}
      aria-current={isCurrent(item.path) ? "page" : undefined}
    >
      {item.label}
    </Link>
  </motion.div>
))}
        </motion.nav>
      </motion.div>

      <motion.div
        className="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        onClick={() => setIsOpen(false)}
      />
    </>
  )}
</AnimatePresence>
    </>
  );
};

export default Header;
